/**
 * Board-keyed reset boundary for the pipeline's two Watchlist-parity filter
 * panels (SE-7407).
 *
 * `MainContainer` stays mounted across a board switch (`activeBoard` is
 * internal `useBoardManager` state, not a route param — there is no
 * `key={boardId}` anywhere up that tree), so any filter state that lived
 * directly in `MainContainer` survived a switch and could narrow or empty a
 * board the user never touched. That was already true of the pre-existing
 * TABLE filter panel (`tableAdvancedFilters`, SE-7144) and became true of the
 * KANBAN board's own panel the moment SE-7405 gave it board-narrowing power.
 *
 * The fix is the repo's sanctioned key-reset wrapper
 * (`.claude/rules/PROJECT_RULES.md`, "react-hooks/set-state-in-effect"): this
 * component owns ONLY the filter-owning state (both panels' committed
 * advanced filters, LISTS selections, and open/closed flags) and nothing
 * else. `MainContainer` renders it with `key={activeBoard?.id}`, so React
 * remounts it — discarding every hook's internal state — on a genuine board
 * change, while `MainContainer` itself (drag state, `useBoardManager`,
 * `useKanbanBoard`, and every other sibling hook) never remounts.
 *
 * Everything this component computes that a SIBLING closure in
 * `MainContainer` also needs (the drag handlers read the Kanban panel's
 * narrowed columns to translate a coordinate-derived drop index — see
 * `resolveRenderedColumn`/`toFullBoardIndex` in `MainContainer.tsx`) is
 * handed back through `children` as a render prop, rather than being read
 * off a module-level export — a remountable child cannot itself be a
 * `useCallback` dependency in the component that outlives it.
 * @module features/player-kanban/components/organisms/BoardFilterScope
 */

import React, { useCallback, useMemo, useState } from "react";

import type {
  KanbanCard,
  PlayerAdvancedFilterInput,
} from "@/generated/graphql";
import useAdvancedFilterState from "@/hooks/useFilterState";
import useFilterVariables from "@/features/scouting/hooks/useFilterVariables";

import { useKanbanBoardFilters } from "../../../hooks/useKanbanBoardFilters";
import type { UseKanbanBoardFiltersResult } from "../../../hooks/useKanbanBoardFilters";
import type { BoardSortState } from "../../../constants/boardSort";
import type { FilterState } from "../../../constants/filterDefaults";
import type { ColumnData } from "../../../types/types";
import type { BoardPlayerMetrics } from "../../../utils/playerFilter";
import { applyBoardSearchAndSort } from "../../../utils/boardSearchSort";
import { applyBoardFiltersToColumns } from "../../../utils/playerFilter";
import {
  applyTableFilterPassSet,
  buildTableFilterPassSet,
} from "../../../utils/tableAdvancedFilter";

/** A single LISTS chip option for the Table filter panel. */
interface TableFilterListOption {
  readonly id: string;
  readonly label: string;
}

/** Everything {@link BoardFilterScopeContainer} hands back to its render-prop children. */
export interface BoardFilterScopeResult {
  /** The KANBAN board's own Watchlist-parity filter panel (SE-7405). */
  readonly kanbanFilters: UseKanbanBoardFiltersResult<ColumnData[]>;
  /** The TABLE view's committed advanced filter state (SE-7144). */
  readonly tableAdvancedFilters: ReturnType<typeof useAdvancedFilterState>;
  /** Whether the Table Filters drawer is open. */
  readonly isTableFilterOpen: boolean;
  /** Opens the Table Filters drawer. */
  readonly onTableFiltersOpen: () => void;
  /** Closes the Table Filters drawer. */
  readonly onTableFiltersClose: () => void;
  /** The Table panel's committed LISTS selection — watchlist ids. */
  readonly tableListSelection: readonly string[];
  /** Commits a new Table LISTS selection. */
  readonly onTableListSelectionCommit: (selection: readonly string[]) => void;
  /** The Table panel's LISTS chip options. */
  readonly tableListOptions: readonly TableFilterListOption[];
  /** `tableColumnsData`, narrowed by search/sort + the committed table filters. */
  readonly parityTableColumnsData: ColumnData[];
  /** Total committed Table filter count, for the toolbar's Filters badge. */
  readonly tableActiveFilterCount: number;
}

/** Props for {@link BoardFilterScopeContainer}. */
export interface BoardFilterScopeProps {
  /** The board's raw columns (search/sort applied — Kanban's own input). */
  readonly visibleColumnsData: ColumnData[];
  /** The Table renderer's raw columns (search/sort NOT yet applied). */
  readonly tableColumnsData: ColumnData[];
  /** The board-scoped search query, for the Table derivation. */
  readonly boardSearchQuery: string;
  /** The board-level sort, for the Table derivation. */
  readonly boardSort: BoardSortState;
  /** The board's raw cards — source for both panels' LISTS options. */
  readonly boardCards: readonly KanbanCard[] | undefined;
  /** SE-6871 PERFORMANCE/GOALKEEPER metrics sidecar, keyed by player id. */
  readonly boardPlayerMetricsById:
    | ReadonlyMap<string, BoardPlayerMetrics>
    | undefined;
  /** The legacy left-rail filter state, read only by the Table derivation. */
  readonly boardFilters: FilterState;
  /** Full GPR span from board data, for the Table derivation's activity detection. */
  readonly dynamicGprRange: [number, number];
  /** Full contract-value span from board data, same purpose as above. */
  readonly dynamicContractValueRange: [number, number];
  /** Render prop receiving the computed {@link BoardFilterScopeResult}. */
  readonly children: (result: BoardFilterScopeResult) => React.ReactNode;
}

/**
 * Derives a watchlist's `{id, label}` option list from the board's cards.
 * Shared shape between the Table and Kanban LISTS chips — a list no card
 * belongs to could only ever produce an empty result, so both derive from
 * the same `boardCards` read.
 * @param boardCards - The board's raw cards.
 * @returns Sorted, de-duplicated LISTS options.
 */
function deriveListOptions(
  boardCards: readonly KanbanCard[] | undefined
): TableFilterListOption[] {
  const entries = (boardCards ?? []).flatMap(card =>
    (card.player?.watchlists ?? []).flatMap(watchlist =>
      watchlist ? [[watchlist.id, watchlist.name] as const] : []
    )
  );
  return Object.entries(Object.fromEntries(entries))
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Owns the Table and Kanban Watchlist-parity filter panels' committed state
 * (SE-7144 / SE-7405) and narrows their respective columns by it.
 *
 * Mounted by `MainContainer` with `key={activeBoard?.id}` — see the module
 * doc for why this is the key-reset boundary rather than an effect.
 * @param props - See {@link BoardFilterScopeProps}.
 * @param props.visibleColumnsData - The board's columns, search/sort applied (Kanban's own input).
 * @param props.tableColumnsData - The Table renderer's raw columns, search/sort NOT yet applied.
 * @param props.boardSearchQuery - The board-scoped search query, for the Table derivation.
 * @param props.boardSort - The board-level sort, for the Table derivation.
 * @param props.boardCards - The board's raw cards — source for both panels' LISTS options.
 * @param props.boardPlayerMetricsById - SE-6871 PERFORMANCE/GOALKEEPER metrics sidecar, keyed by player id.
 * @param props.boardFilters - The legacy left-rail filter state, read only by the Table derivation.
 * @param props.dynamicGprRange - Full GPR span from board data, for the Table derivation's activity detection.
 * @param props.dynamicContractValueRange - Full contract-value span from board data, same purpose as above.
 * @param props.children - Render prop receiving the computed {@link BoardFilterScopeResult}.
 * @returns The result of calling `children` with the computed filter state.
 */
const BoardFilterScopeContainer = ({
  visibleColumnsData,
  tableColumnsData,
  boardSearchQuery,
  boardSort,
  boardCards,
  boardPlayerMetricsById,
  boardFilters,
  dynamicGprRange,
  dynamicContractValueRange,
  children,
}: BoardFilterScopeProps): React.ReactNode => {
  // ── SE-7144: the TABLE view's Watchlist-parity filter panel. ──
  const tableAdvancedFilters = useAdvancedFilterState();
  const [isTableFilterOpen, setIsTableFilterOpen] = useState(false);
  const [tableListSelection, setTableListSelection] = useState<
    readonly string[]
  >([]);
  const onTableFiltersOpen = useCallback(() => setIsTableFilterOpen(true), []);
  const onTableFiltersClose = useCallback(
    () => setIsTableFilterOpen(false),
    []
  );
  const tableAdvancedInput = useFilterVariables(
    tableAdvancedFilters,
    ""
  ) as PlayerAdvancedFilterInput;

  const tableListOptions = useMemo(
    () => deriveListOptions(boardCards),
    [boardCards]
  );

  // SE-6871: the TABLE renderer additionally applies the committed board
  // filters to its rows — mirrors the derivation MainContainer used to run
  // inline (see the module doc's history note).
  const visibleTableColumnsData = useMemo(
    () =>
      applyBoardFiltersToColumns(
        applyBoardSearchAndSort(tableColumnsData, boardSearchQuery, boardSort),
        boardFilters,
        dynamicGprRange,
        dynamicContractValueRange,
        boardPlayerMetricsById
      ),
    [
      tableColumnsData,
      boardSearchQuery,
      boardSort,
      boardFilters,
      dynamicGprRange,
      dynamicContractValueRange,
      boardPlayerMetricsById,
    ]
  );

  const tableFilterPassSet = useMemo(
    () =>
      buildTableFilterPassSet({
        players: (boardCards ?? []).flatMap(card =>
          card.player ? [card.player] : []
        ),
        metricsById: boardPlayerMetricsById,
        advancedInput: tableAdvancedInput,
        listSelection: tableListSelection,
        activeFilterCount: tableAdvancedFilters.activeFilterCount,
      }),
    [
      boardCards,
      boardPlayerMetricsById,
      tableAdvancedInput,
      tableListSelection,
      tableAdvancedFilters.activeFilterCount,
    ]
  );

  const parityTableColumnsData = useMemo(
    () => applyTableFilterPassSet(visibleTableColumnsData, tableFilterPassSet),
    [visibleTableColumnsData, tableFilterPassSet]
  );

  const tableActiveFilterCount =
    tableAdvancedFilters.activeFilterCount +
    (tableListSelection.length > 0 ? 1 : 0);

  // ── SE-7405: Kanban's own Watchlist-parity filter panel. ──
  const kanbanFilters = useKanbanBoardFilters(
    visibleColumnsData,
    boardCards,
    boardPlayerMetricsById
  );

  return children({
    kanbanFilters,
    tableAdvancedFilters,
    isTableFilterOpen,
    onTableFiltersOpen,
    onTableFiltersClose,
    tableListSelection,
    onTableListSelectionCommit: setTableListSelection,
    tableListOptions,
    parityTableColumnsData,
    tableActiveFilterCount,
  });
};

export default BoardFilterScopeContainer;
